// Fontaine M28/M29 — the ONE navigation model. Sidebar (laptop), bottom tab bar + More sheet (phone),
// command palette and breadcrumbs all read this. Icons are Lucide names (components/ui/Icon).
// `feature` = App\Constants\Features key: when the plan lacks it the item shows LOCKED (never hidden).
// `hideLocked` = HIDDEN rather than locked when the plan lacks `feature` (Alex, 2026-10-27).
// `masterOnly` = RC master admins only, and HIDDEN (not locked) for everyone else: the row leads to
// data that is not the merchant's and no plan can unlock, so "included in a higher plan" would lie.
// `pageLocked` = locked for EVERYONE whatever the plan (core/config/dashboard.js): shown with a lock,
// never a link. The route itself redirects away while the lock holds.
// `opened` = was masterOnly, now sold through the Feature Matrix: HIDDEN unless the user can use ANY of
// these keys (canUseOpenedFeature — master admins, or a merchant on an enforcing store that has it).
// `feature` may be "a|b": unlocked by EITHER key.

import { DASHBOARD_PATH, DASHBOARD_LOCKED } from "../../core/config/dashboard.js";

/** The Deals tabs' Feature Matrix keys, in tab order (views/Deals/DealsPage.jsx). */
export const DEALS_FEATURES = ["buyops_ch24_private_sellers", "deals_auctions", "buyops_underpriced_alerts"];
// "Automated private seller contact" lives inside those tabs, so it does not open Deals on its own.

/**
 * Should this nav row be left out for this user? `canOpen(key)` answers for an
 * `opened` key — pass `(k) => canUseOpenedFeature(user, entitlements, k)`;
 * `hasFeature` (optional) applies `hideLocked`.
 */
export const isNavHidden = (item, isMasterAdmin, canOpen = () => isMasterAdmin, hasFeature) =>
  (!!item.masterOnly && !isMasterAdmin) ||
  (!!item.opened && !item.opened.some(canOpen)) ||
  (!!item.hideLocked && !!hasFeature && !hasFeature(item.feature));

export const NAV_PRIMARY = [
  // "Dashboard" — was "Overview" at /rc/overview until 2026-10-20, and locked
  // until we choose to open it (Alex). The earnings page that held this name
  // is "Earnings" now, in NAV_SECONDARY.
  { id: "dashboard", label: "Dashboard", to: DASHBOARD_PATH, icon: "Home", pageLocked: DASHBOARD_LOCKED },
  { id: "listings", label: "Listings", to: "/rc/listings", icon: "LayoutGrid", shortcut: "l" },
  // No "Order / Sell" row (Guillaume, 2026-10-13): the request flows are
  // reached from the Orders page's + Sourcing / + Sell Enquiry buttons, so
  // they (`also`) light Orders.
  { id: "orders", label: "Orders", to: "/requests/orders", also: ["/request"], icon: "Receipt", shortcut: "o" },
  { id: "kanban", label: "Kanban", to: "/kanban", icon: "SlidersHorizontal", feature: "kanban_list_view", hideLocked: true, shortcut: "k" },
  { id: "users", label: "Users", to: "/users/client", icon: "Users", shortcut: "u" },
  // Leads are what the WhatsApp group ingestion produces, so they are sold with it (Lead Gen).
  { id: "leads", label: "Leads", to: "/leads/leads", icon: "Inbox", feature: "wa_group_ingestion", hideLocked: true, shortcut: "e" },
  // The raw WhatsApp intake, upstream of Leads: every message our Cooby seats
  // see, before the ETL maps any of it to a product. Was masterOnly; sold as
  // "Trade Feed & assistant" since 2026-10-27 (Alex).
  { id: "tradeFeed", label: "Trade Feed", to: "/trade-feed", icon: "Activity", opened: ["trade_feed"], shortcut: "f" },
  // Buy Alerts has left this registry (Alex, 2026-10-08): Core List is where
  // every buy watch lives now, so a second entry for the same job was two
  // places to look. /alerts still RESOLVES — the page and its data are
  // untouched — it is simply no longer offered in the navigation.
  //
  // "Stock Alert", not "Stock": the entry names the alerting rather than the
  // inventory, because that is what the page is for once marketplaces land.
  //
  // Stock alert (Core List, Ad List, Sales Pipeline) OR Geographic Arbitrages
  // opens it — a store with only the latter lands on the Arbitrages tab.
  { id: "stock", label: "Stock Alert", to: "/stock/core-list", icon: "Package", feature: "core_inventory_management|geo_arbitrage", badge: "stock", shortcut: "s" },
  // Deals — overnight buy opportunities (private sellers, auctions ending soon,
  // 10% below trade). Was masterOnly; one Feature Matrix row per tab since
  // 2026-10-27 (Alex). The page opens on the first tab the user has.
  { id: "deals", label: "Deals", to: "/deals/private-sellers", also: ["/deals"], icon: "Tag", opened: DEALS_FEATURES },
  { id: "analytics", label: "Analytics", to: "/analytics", icon: "Monitor", feature: "analytics_core", hideOnMaster: true, shortcut: "n" },
];

// Analytics quick-nav — the reports a rep actually opens, under the Analytics
// row (Guillaume, 2026-09-01: "those names only, the rest dont add").
// P&L and Reconciliation are their own routes; Marketing is an Analytics tab.
// Store Performance and Sales Executive left with Dashboard v2 (2026-10-21):
// they are the Dashboard's Store and My Day tabs now, and their old
// /analytics URLs redirect there (layouts/Routes.jsx). Deliberately NOT the
// full `TABS` list in analytics/AnalyticsPage.jsx — a shortcut list, not a
// mirror of that page.
export const NAV_ANALYTICS = [
  { id: "analytics-pnl", label: "P&L", to: "/pnl", icon: "Banknote", feature: "accounting_pnl_reconciliation" },
  { id: "analytics-reconciliation", label: "Reconciliation", to: "/reconciliation", icon: "CheckCircle2", feature: "accounting_pnl_reconciliation" },
  { id: "analytics-marketing", label: "Marketing", to: "/analytics/marketing", icon: "Globe", feature: "analytics_marketing" },
];

// The store's OWN public website, opened from the app — "Retail Circle is
// software to manage my shop, I should be able to see my own shop and all of its
// pages natively here" (Guillaume, 2026-09-01).
//
// `path` is appended to the store's `store_url` (app settings; DAO's is
// https://daowatches.com), so nothing here is tenant-specific and no URL is
// hardcoded — a store with no `store_url` simply shows no group. These are the
// storefront's own routes, the same ones the old hardcoded "Dao Watches" group
// pointed at internally; they now open the live site in a new tab.
export const NAV_STOREFRONT = [
  { id: "site-home", label: "Home", path: "", icon: "Home" },
  { id: "site-shop", label: "Shop", path: "/products/watches", icon: "ShoppingBag" },
  { id: "site-wishlist", label: "Wishlist", path: "/products/wishlist", icon: "Star" },
  { id: "site-consignment", label: "Consignment", path: "/consignment", icon: "Package" },
  { id: "site-guidebook", label: "Guidebook", path: "/member-guidebook", icon: "FileText" },
];

/** The storefront rows for one store — absolute links, or [] with no site set. */
export const storefrontNav = (storeUrl) => {
  if (!storeUrl) return [];
  const base = String(storeUrl).replace(/\/+$/, "");
  return NAV_STOREFRONT.map((i) => ({ ...i, to: `${base}${i.path}`, external: true }));
};

export const NAV_SECONDARY = [
  { id: "earnings", label: "Earnings", to: "/earnings", icon: "Sparkles", feature: "earnings" },
  { id: "messages", label: "Messages", to: "/messages", icon: "MessageCircle", badge: "messages" },
];

// Reachable, but not a sidebar row. Notifications lives on the topbar bell —
// same count, same `/notifications` page — so a sidebar row was a second entry
// point for one destination (2026-09-01). The phone has no bell, so it stays in
// the "More" sheet, and the route is untouched.
export const NAV_PHONE_ONLY = [
  { id: "notifications", label: "Notifications", to: "/notifications", icon: "Inbox", badge: "notifications" },
];

export const NAV_BOTTOM = [
  // `Zap` was never in the icon registry, so this rendered a label with no
  // glyph beside it — the only item in the sidebar without one (Alex,
  // 2026-09-25). A heartbeat rather than a lightning bolt: an uptime check IS
  // a heartbeat, and it keeps the bolt free for anything about speed.
  // masterOnly: the board is every tenant's errors and /api/monitoring is
  // master.admin — the plan feature alone left the row on for everyone while
  // gating is off (Alex, 2026-10-27).
  { id: "uptime", label: "Uptime", to: "/uptime", icon: "HeartPulse", feature: "uptime_monitor", masterOnly: true },
  // Scraping Detection Alerts & User Analytics — per-user API usage across
  // every store, so masterOnly (Alex, 2026-10-06).
  { id: "usageAnalytics", label: "Usage Analytics", to: "/usage-analytics", icon: "Radar", masterOnly: true },
  // Profile was only ever reachable from the avatar menu in the top-right
  // corner, which is now gone from both shells (Alex, 2026-09-25). It sits
  // directly above Settings because the two are the same kind of destination —
  // yours and the store's — and that is where people went looking for it.
  { id: "profile", label: "Profile", to: "/profile", icon: "User" },
  // Its own page, out of the Settings tabs (Alex, 2026-10-16). `waBroadcast`
  // gates it like the page does: owners and store admins of the active store,
  // and master admins (canUseWaBroadcast; the backend checks the same).
  { id: "waBroadcast", label: "WhatsApp Broadcast", to: "/whatsapp-broadcast", icon: "MessageCircle", waBroadcast: true },
  { id: "settings", label: "Settings", to: "/store-settings", icon: "Settings", shortcut: "," },
  { id: "logout", label: "Log out", to: "/logout", icon: "LogOut" },
];

// DAO Watches group — only the DAO store itself or RC master admins.
export const NAV_DAO = [
  { id: "shop", label: "Shop", to: "/products/watches", icon: "ShoppingBag" },
  { id: "wishlist", label: "Wishlist", to: "/products/wishlist", icon: "Heart", feature: "wishlist" },
  { id: "consignment", label: "Consignment", to: "/consignment", icon: "Package" },
  { id: "guidebook", label: "Guidebook", to: "/member-guidebook", icon: "FileText" },
];

// Phone: 4 tabs + More (Guillaume, shell mock 2026-08-26).
//
// Trade Feed holds the fourth slot instead of Buy Alerts: it is checked many
// times a day and was two taps away behind More, which is the wrong cost for
// the most-used page on a phone.
//
// It is masterOnly, though, and the tab bar does NOT filter on that the way the
// More sheet does — so listing it here unguarded would show every user a tab
// leading to a page the API refuses. `phoneTabIds` below is the guard: anyone
// who cannot open Trade Feed keeps Stock Alert in that slot.
export const PHONE_TABS = ["listings", "orders", "stock", "tradeFeed"];

// MoreSheet drops whichever page the tab bar is already showing, so nothing is
// duplicated and nothing is unreachable.
export const PHONE_MORE = ["kanban", "users", "leads", "tradeFeed", "analytics", "stock", "earnings", "dashboard", "messages", "notifications", "profile", "waBroadcast", "settings", "logout"];

// If a phone tab is master-only and this user is not a master admin, what takes
// its place. A missing entry means the slot is simply dropped.
// Trade Feed is master-only; everyone else gets Stock Alert in that slot.
// It used to fall back to Buy Alerts, which no longer exists in this registry.
// Stock Alert already holds the third slot since Order / Sell folded into
// Orders, so the fallback is Leads.
const PHONE_TAB_FALLBACK = { tradeFeed: "leads" };

/**
 * The four phone tab ids for this user.
 *
 * Shared by the tab bar and the More sheet so the two cannot disagree about
 * which page lives where — the failure that would otherwise show a page twice
 * or not at all.
 *
 * @param {boolean} isMasterAdmin
 * @param {(key: string) => boolean} [canOpen] for `opened` rows; see isNavHidden
 * @returns {string[]}
 */
export const phoneTabIds = (isMasterAdmin, canOpen) =>
  PHONE_TABS.map((id) => {
    const item = navById(id);
    if (item && isNavHidden(item, isMasterAdmin, canOpen)) {
      return PHONE_TAB_FALLBACK[id] ?? null;
    }
    return id;
  }).filter(Boolean);

export const ALL_NAV = [...NAV_PRIMARY, ...NAV_ANALYTICS, ...NAV_SECONDARY, ...NAV_PHONE_ONLY, ...NAV_BOTTOM, ...NAV_DAO];
export const navById = (id) => ALL_NAV.find((n) => n.id === id);

/** Longest-prefix match so /requests/orders wins over /request. */
export const activeNavId = (pathname) => {
  let best = null;
  let bestLen = 0;
  for (const n of ALL_NAV) {
    for (const to of [n.to, ...(n.also || [])]) {
      if ((pathname === to || pathname.startsWith(`${to}/`)) && to.length > bestLen) {
        best = n;
        bestLen = to.length;
      }
    }
  }
  return best?.id ?? null;
};
